import React, { useState, useEffect } from 'react';
import { db } from '../services/db';
import { TimeEntry, Project } from '../models/types';
import { format } from 'date-fns';
import { blockUpdates, allowUpdates } from '../utils/updateGate';
import { autoSync } from '../services/syncService';
import '../styles/Modal.css';

interface EditEntryModalProps {
  entry: TimeEntry;
  onClose: () => void;
}

const EditEntryModal: React.FC<EditEntryModalProps> = ({ entry, onClose }) => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [tasks, setTasks] = useState<string[]>([]);
  const [showDurationPicker, setShowDurationPicker] = useState(false);
  const [lunchDuration, setLunchDuration] = useState(30); // Default 30 minutes
  const [showLocation, setShowLocation] = useState(true);
  const [enableOvernight, setEnableOvernight] = useState(false);
  
  // Split date and time states
  const [startDate, setStartDate] = useState('');
  const [startTimeIn, setStartTimeIn] = useState('');
  const [endDate, setEndDate] = useState('');
  const [endTimeOut, setEndTimeOut] = useState('');
  
  const [formData, setFormData] = useState({
    project: '',
    task: '',
    location: '',
    notes: ''
  });

  useEffect(() => {
    blockUpdates();
    return () => { allowUpdates(); };
  }, []);

  useEffect(() => {
    loadProjects();

    // Load settings
    const storedShowLocation = localStorage.getItem('showLocationButtons');
    if (storedShowLocation !== null) {
      setShowLocation(storedShowLocation === 'true');
    }
    
    const storedEnableOvernight = localStorage.getItem('enableOvernightShifts');
    if (storedEnableOvernight !== null) {
      setEnableOvernight(storedEnableOvernight === 'true');
    }
    
    // Initialize split date/time fields
    if (entry.timeIn) {
      setStartDate(format(entry.timeIn, "yyyy-MM-dd"));
      setStartTimeIn(format(entry.timeIn, "HH:mm"));
    }
    
    if (entry.timeOut) {
      setEndDate(format(entry.timeOut, "yyyy-MM-dd"));
      setEndTimeOut(format(entry.timeOut, "HH:mm"));
    } else {
      setEndDate(format(entry.timeIn, "yyyy-MM-dd"));
      setEndTimeOut('');
    }
    
    setFormData({
      project: entry.project,
      task: entry.task || '',
      location: entry.location,
      notes: entry.notes || ''
    });
  }, [entry]);

  useEffect(() => {
    if (formData.project) {
      const project = projects.find(p => p.name === formData.project);
      if (project) {
        setTasks(project.tasks);
      }
    }
  }, [formData.project, projects]);

  const loadProjects = async () => {
    try {
      const allProjects = await db.projects.toArray();
      setProjects(allProjects);
    } catch (error) {
      console.error('Error loading projects:', error);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      if (!startDate || !startTimeIn) {
        alert('Start Date and Time In are required');
        return;
      }

      const timeIn = new Date(`${startDate}T${startTimeIn}:00`);
      let timeOut: Date | undefined = undefined;
      
      if (endTimeOut) {
        // Use endDate if overnight enabled, otherwise assume same day as startDate
        const dateOut = enableOvernight ? (endDate || startDate) : startDate;
        timeOut = new Date(`${dateOut}T${endTimeOut}:00`);
        
        if (timeOut <= timeIn) {
           alert('Time Out must be after Time In');
           return;
        }
      }

      await db.updateTimeEntry(entry.id!, {
        timeIn,
        timeOut,
        project: formData.project,
        task: formData.task || undefined,
        location: formData.location,
        notes: formData.notes || undefined,
        // Saving un-deletes. A tombstoned entry is filtered out of every view,
        // so reaching this line normally means it came back from a backup or
        // from a delete the office refused — and if the tombstone survived the
        // save, the next sync would send `{deleted:true}` and discard the edit
        // without saying so. (Dexie treats `undefined` as "remove this
        // property", which is exactly the clear this needs.)
        deletedAt: undefined
      });

      // An edit resets the entry to unsynced (updateTimeEntry bumps
      // lastModified past syncedAt), so the dashboard is now stale until
      // something pushes. Without this the correction sits behind the next
      // clock-out or app open — and a correction is usually made precisely
      // because the office is about to look at the number.
      autoSync().catch((err) => console.error('Auto-sync after entry edit failed:', err));

      onClose();
    } catch (error) {
      console.error('Error updating time entry:', error);
      alert('Failed to update time entry');
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this time entry?')) {
      try {
        // An entry the dashboard has never seen can just go. Anything it HAS
        // seen becomes a tombstone, so the deletion reaches the dashboard
        // instead of the two silently disagreeing forever.
        if (entry.syncedAt) {
          await db.timeEntries.update(entry.id!, {
            deletedAt: new Date(),
            lastModified: new Date(),
          });
          // A tombstone is unsynced work like any other, and the whole point
          // of writing one is that the deletion reaches the office. Only the
          // tombstone branch needs this: the other branch removed an entry the
          // dashboard has never seen, so there is nothing to tell it.
          autoSync().catch((err) => console.error('Auto-sync after entry delete failed:', err));
        } else {
          await db.timeEntries.delete(entry.id!);
        }
        onClose();
      } catch (error) {
        console.error('Error deleting time entry:', error);
        alert('Failed to delete time entry');
      }
    }
  };

  const handleApplyLunchBreak = () => {
    if (!endTimeOut) {
      alert('Please set a Time Out before applying a lunch break');
      return;
    }
    
    setShowDurationPicker(true);
  };

  const handleDurationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLunchDuration(parseInt(e.target.value, 10));
  };

  const confirmLunchBreak = () => {
    if (lunchDuration <= 0) {
      alert('Lunch duration must be greater than 0 minutes');
      return;
    }
    
    // Add lunch break note
    const lunchNote = `Deducted ${lunchDuration} min for Lunch`;
    const updatedNotes = formData.notes 
      ? `${formData.notes}\n${lunchNote}`
      : lunchNote;
    
    // Update form data with lunch break note
    setFormData(prev => ({
      ...prev,
      notes: updatedNotes
    }));
    
    setShowDurationPicker(false);
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <h2>Edit Time Entry</h2>
        
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="startDate">Date:</label>
            <input
              type="date"
              id="startDate"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="timeIn">Time In:</label>
            <input
              type="time"
              id="timeIn"
              value={startTimeIn}
              onChange={(e) => setStartTimeIn(e.target.value)}
              required
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="timeOut">Time Out:</label>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              {enableOvernight && (
                <input
                  type="date"
                  id="endDate"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  min={startDate}
                  style={{ flex: 1 }}
                />
              )}
              <input
                type="time"
                id="timeOut"
                value={endTimeOut}
                onChange={(e) => setEndTimeOut(e.target.value)}
                style={{ flex: enableOvernight ? 1 : 'initial', width: enableOvernight ? 'auto' : '100%' }}
              />
            </div>
          </div>
          
          <div className="form-group">
            <label htmlFor="project">Project:</label>
            <select
              id="project"
              name="project"
              value={formData.project}
              onChange={handleChange}
              required
            >
              {projects.map(project => (
                <option key={project.name} value={project.name}>
                  {project.name}
                </option>
              ))}
            </select>
          </div>
          
          <div className="form-group">
            <label htmlFor="task">Task:</label>
            <input
              type="text"
              id="task"
              name="task"
              list="tasks-list"
              value={formData.task}
              onChange={handleChange}
            />
            <datalist id="tasks-list">
              {tasks.map(task => (
                <option key={task} value={task} />
              ))}
            </datalist>
          </div>
          
          {showLocation && (
            <div className="form-group">
              <label htmlFor="location">Location:</label>
              <div className="location-toggle">
                <button
                  type="button"
                  className={formData.location === 'Site' ? 'active' : ''}
                  onClick={() => setFormData(prev => ({ ...prev, location: 'Site' }))}
                >
                  Site
                </button>
                <button
                  type="button"
                  className={formData.location === 'Shop' ? 'active' : ''}
                  onClick={() => setFormData(prev => ({ ...prev, location: 'Shop' }))}
                >
                  Shop
                </button>
              </div>
            </div>
          )}
          
          <div className="form-group">
            <label htmlFor="notes">Notes:</label>
            <textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleChange}
            />
          </div>
          
          <div className="form-actions">
            <button 
              type="button" 
              className="lunch-break-btn"
              onClick={handleApplyLunchBreak}
              disabled={!endTimeOut}
              title="Add lunch break to this time entry"
            >
              🍽️ Add Lunch Break
            </button>

            <div className="modal-buttons-vertical">
              <button type="submit" className="save-btn">
                Save
              </button>
              <button type="button" className="delete-btn" onClick={handleDelete}>
                Delete
              </button>
              <button type="button" className="cancel-btn" onClick={onClose}>
                Cancel
              </button>
            </div>
          </div>

          {showDurationPicker && (
            <div className="duration-picker-overlay">
              <div className="duration-picker">
                <h3>Select Lunch Duration</h3>
                <div className="duration-slider">
                  <input
                    type="range"
                    min="15"
                    max="120"
                    step="15"
                    value={lunchDuration}
                    onChange={handleDurationChange}
                    className="duration-range"
                  />
                  <div className="duration-value">{lunchDuration} minutes</div>
                </div>
                <div className="duration-actions">
                  <button 
                    type="button" 
                    className="cancel-btn" 
                    onClick={() => setShowDurationPicker(false)}
                  >
                    Cancel
                  </button>
                  <button 
                    type="button" 
                    className="save-btn" 
                    onClick={confirmLunchBreak}
                  >
                    Apply
                  </button>
                </div>
              </div>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default EditEntryModal;
